import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./Note.scss";
import { BiEdit, BiTrash, BiCalendar, BiHash } from "react-icons/bi";
import { deleteNote, getNoteByUser } from "../../services/api/NoteApi";
import { useLoading } from "../../context/LoadingContext";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import { useSearchResults } from "../../context/SearchResultsContext";
import { getBothCourtCasesDetailsByCourtRef } from "../../services/api/CasesApi";
import { WrittenFormat } from "../../components/WrittenFormat";
import { IoGrid } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";
import { useToast } from "../../context/ToastContext";
import { formatPartyNames, formatText } from "../../components/TruncateParties";
import { useNoteSelection } from "../../context/NoteSelectionContext";

export const Notes = () => {
  const [notes, setNotes] = useState([]);
  const { showToast } = useToast();
  const { searchResults, searchType } = useSearchResults();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const { showLoading, hideLoading } = useLoading();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Use context for all filter and search management
  const {
    searchTerm: noteSearchTerm,
    setSearchTerm: setNoteSearchTerm,
    filters: noteFilters,
    setFilters: setNoteFilters,
    filteredNotes,
    setFilteredNotes,
    clearAllFilters: clearNoteFilters,
    hasActiveFilters: hasNoteFilters,
    buildQueryParams,
    triggerNoteRefresh,
    pagination,
    setPagination,
    updatePaginationInfo,
  } = useNoteSelection();

  // Display notes logic - use filteredNotes from context
  const displayNotes = useMemo(() => {
    console.log(
      "Using filteredNotes from context:",
      filteredNotes?.length || 0
    );
    return filteredNotes || [];
  }, [filteredNotes]);

  const isInSearchMode = searchType === "notes";

  const shouldShowNoDataMessage = () => {
    const hasContextFilters = hasNoteFilters;
    if (hasContextFilters && displayNotes.length === 0) return true;
    return displayNotes.length === 0;
  };

  const getNoDataMessage = () => {
    const hasContextFilters = hasNoteFilters;
    if (hasContextFilters && displayNotes.length === 0) {
      return "No notes found matching your search and filters.";
    }
    return "No notes found. Start by creating a new one!";
  };

  // Initial fetch effect
  // useEffect(() => {
  //     const fetchInitialNotes = async () => {
  //         // Only fetch initial data if we don't have any filtered notes yet
  //         if (filteredNotes.length > 0) {
  //             console.log('Using existing filtered notes from context');
  //             setNotes(filteredNotes);
  //             return;
  //         }

  //         try {
  //             showLoading();
  //             const response = await getNoteByUser();
  //             console.log("Initial note fetch:", response.data.notes?.length || 0);
  //             const noteData = response.data.notes || [];

  //             setNotes(noteData);

  //             // Set initial filtered notes in context only if no filters are active
  //             if (!hasNoteFilters) {
  //                 setFilteredNotes(noteData);
  //             }
  //         } catch (err) {
  //             setError(err.message || "Failed to fetch notes.");
  //             console.error("Error fetching notes:", err);
  //         } finally {
  //             hideLoading();
  //         }
  //     };

  //     fetchInitialNotes();
  // }, []);

  // Fetch notes when filters change
  useEffect(() => {
    const fetchFilteredNotes = async () => {
      if (!hasNoteFilters) return; // Don't fetch if no filters are active

      try {
        showLoading();
        const queryString = buildQueryParams();
        console.log("Fetching notes with filters:", queryString);

        const response = await getNoteByUser(
          queryString ? `?${queryString}` : ""
        );
        const noteData = response.data.notes || [];

        console.log("Filtered note fetch result:", noteData.length);
        setFilteredNotes(noteData);
      } catch (err) {
        console.error("Error fetching filtered notes:", err);
        setError(err.message || "Failed to fetch notes.");
      } finally {
        hideLoading();
      }
    };

    fetchFilteredNotes();
  }, [noteFilters, noteSearchTerm, hasNoteFilters, buildQueryParams]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  };

  const handleEdit = (note) => {
    navigate(`/home/notes/edit-note/${note._id}`, {
      state: { noteData: note },
    });
  };

  const handleDeleteClick = (noteId) => {
    setNoteToDelete(noteId);
    setIsDeleteModalOpen(true);
  };

  const handleNotesPerPageChange = useCallback(
    (e) => {
      const newNotesPerPage = Number(e.target.value);
      setPagination((prev) => ({
        ...prev,
        limit: newNotesPerPage,
        page: 0,
      }));
    },
    [setPagination]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      setPagination((prev) => ({
        ...prev,
        page: newPage,
      }));
    },
    [setPagination]
  );

  const confirmDelete = async () => {
    try {
      showLoading();
      await deleteNote(noteToDelete);

      // Update both local state and context
      // setNotes(prev => prev.filter(n => n._id !== noteToDelete));
      // const updatedFiltered = filteredNotes.filter(n => n._id !== noteToDelete);
      // setFilteredNotes(updatedFiltered);
      // triggerNoteRefresh()

      if (filteredNotes.length === 1 && pagination.page > 0) {
        // Go back to the previous page. This will trigger the useEffect to refetch.
        setPagination((prev) => ({
          ...prev,
          page: prev.page - 1,
        }));
      } else {
        // Otherwise, just refresh the current page's data using the trigger.
        triggerNoteRefresh();
      }
      showToast("Note deleted successfully", "success");
    } catch (err) {
      console.error("Delete failed:", err);
      showToast("Failed to delete note. Please try again.", "error");
    } finally {
      hideLoading();
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setNoteToDelete(null);
  };

  // Fetch case details for notes with courtRef
  useEffect(() => {
    const fetchCaseDetails = async (courtRef) => {
      if (!courtRef) return;
      setLoading(true);
      setError(null);
      try {
        const response = await getBothCourtCasesDetailsByCourtRef(courtRef);
        setCaseData((prevData) => ({
          ...prevData,
          [courtRef]: response.data,
        }));
      } catch (err) {
        console.error("Error fetching case details:", err);
        setError(err.response?.data?.message || "Failed to fetch case details");
      } finally {
        setLoading(false);
      }
    };

    // Fetch case details for each note with a courtRef
    displayNotes.forEach((note) => {
      if (note.courtRef) {
        fetchCaseDetails(note.courtRef);
      }
    });
  }, [displayNotes]);

  // Filter dropdown positioning
  const filterButtonRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;

      let leftPosition = buttonRect.left;

      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    // Add scroll handler
    const handleScroll = () => {
      if (isFilterOpen) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  return (
    <>
      <div className={`note-container`}>
        <div className="invoices-header">
          <div className="view-toggle-wrapper">
            <button
              className={`view-toggle-btn ${
                viewMode === "grid" ? "active" : ""
              }`}
              onClick={() => setViewMode("grid")}
            >
              <IoGrid />
            </button>
            <button
              className={`view-toggle-btn ${
                viewMode === "table" ? "active" : ""
              }`}
              onClick={() => setViewMode("table")}
            >
              <FaTableList />
            </button>

            <div className="common-pagination-settings">
              <label htmlFor="notePerPageSelect">Notes per page:</label>
              <select
                id="notePerPageSelect"
                value={pagination.limit}
                onChange={handleNotesPerPageChange}
              >
                {[5, 10, 15, 20, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-dropdown-wrapper">
            <button
              onClick={() => setIsFilterOpen((prev) => !prev)}
              className="filter-toggle-btn"
              ref={filterButtonRef}
            >
              {isFilterOpen ? "Hide Filters" : "Show Filters"}
            </button>
            {isFilterOpen && (
              <div
                className="filter-dropdown-box"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  opacity: dropdownPosition.top > 0 ? 1 : 0,
                  visibility: dropdownPosition.top > 0 ? "visible" : "hidden",
                }}
              >
                <div className="filter-group">
                  <label>Title</label>
                  <input
                    type="text"
                    placeholder="Enter Note Title"
                    value={noteFilters.title || ""}
                    onChange={(e) =>
                      setNoteFilters({
                        ...noteFilters,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="filter-group">
                  <label>Content</label>
                  <input
                    type="text"
                    placeholder="Enter Content"
                    value={noteFilters.content || ""}
                    onChange={(e) =>
                      setNoteFilters({
                        ...noteFilters,
                        content: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="filter-group">
                  <label>From Date</label>
                  <input
                    type="date"
                    value={noteFilters.fromDate || ""}
                    onChange={(e) =>
                      setNoteFilters({
                        ...noteFilters,
                        fromDate: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="filter-group">
                  <label>To Date</label>
                  <input
                    type="date"
                    value={noteFilters.toDate || ""}
                    onChange={(e) =>
                      setNoteFilters({
                        ...noteFilters,
                        toDate: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="filter-actions">
                  <button
                    className="clear-btn"
                    onClick={() => clearNoteFilters()}
                  >
                    Clear
                  </button>
                  <button
                    className="hide-btn"
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Hide
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* {hasNoteFilters && (
                    <div className="applied-filters">
                        <strong>Applied Filters:</strong>
                        {noteSearchTerm && <span>Search: {noteSearchTerm}</span>}
                        {noteFilters.title && <span>Title: {noteFilters.title}</span>}
                        {noteFilters.content && <span>Content: {noteFilters.content}</span>}
                        {noteFilters.fromDate && <span>From: {noteFilters.fromDate}</span>}
                        {noteFilters.toDate && <span>To: {noteFilters.toDate}</span>}
                    </div>
                )} */}
        {hasNoteFilters && (
          <div className="applied-filters">
            <span className="filters-text">Applied Filters:</span>

            {/* Search term filter pill */}
            {noteSearchTerm && noteSearchTerm.trim() && (
              <span
                className="filter-tag"
                onClick={() => {
                  setNoteSearchTerm("");
                  // Clear DetailSection's search input
                  if (window.clearDetailSectionSearch) {
                    window.clearDetailSectionSearch();
                  }
                }}
              >
                Search: {formatText(noteSearchTerm)} ×
              </span>
            )}

            {/* Individual filter pills */}
            {noteFilters.title && (
              <span
                className="filter-tag"
                onClick={() => setNoteFilters({ ...noteFilters, title: "" })}
              >
                Title: {formatText(noteFilters.title)} ×
              </span>
            )}

            {noteFilters.content && (
              <span
                className="filter-tag"
                onClick={() => setNoteFilters({ ...noteFilters, content: "" })}
              >
                Content: {formatText(noteFilters.content)} ×
              </span>
            )}

            {noteFilters.fromDate && (
              <span
                className="filter-tag"
                onClick={() => setNoteFilters({ ...noteFilters, fromDate: "" })}
              >
                From: {formatText(noteFilters.fromDate)} ×
              </span>
            )}

            {noteFilters.toDate && (
              <span
                className="filter-tag"
                onClick={() => setNoteFilters({ ...noteFilters, toDate: "" })}
              >
                To: {formatText(noteFilters.toDate)} ×
              </span>
            )}

            {/* Clear All button */}
            <span
              className="filter-tag clear-all-tag"
              onClick={() => {
                clearNoteFilters();
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Clear All
            </span>
          </div>
        )}
        <div className="note-grid">
          {shouldShowNoDataMessage() ? (
            <p className="no-notes-found">{getNoDataMessage()}</p>
          ) : viewMode === "grid" ? (
            displayNotes.map((note) => (
              <div
                key={note._id}
                className="note-main-card"
                onClick={() =>
                  navigate(`/home/notes/details/${note._id}`, {
                    state: {
                      noteData: note,
                      parties: caseData?.[note.courtRef]?.case?.parties || null,
                    },
                  })
                }
              >
                <div className="note-main-card-header">
                  <div className="note-title-group">
                    <h3 className="note-title">{note.title}</h3>
                  </div>
                  <div className="note-main-card-actions">
                    <button
                      className="action-button edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(note);
                      }}
                      aria-label="Edit note"
                    >
                      <BiEdit size={16} />
                    </button>
                    <button
                      className="action-button delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(note._id);
                      }}
                      aria-label="Delete note"
                    >
                      <BiTrash size={16} />
                    </button>
                  </div>
                </div>
                {note.courtRef && (
                  <span
                    className="note-courtRef-tag"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/home/cases/casedetails/${note.courtRef}`);
                    }}
                  >
                    {caseData && caseData[note.courtRef]?.case?.parties
                      ? formatPartyNames(caseData[note.courtRef].case.parties)
                      : "Parties not available"}
                    -({note.courtRef})
                  </span>
                )}
                <div className="note-content-area">
                  <p className="note-content">{WrittenFormat(note.content)}</p>
                </div>
                <div className="note-footer">
                  <span className="created-date">
                    Created {formatDate(note.createdAt)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <table className="notes-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Created</th>
                  <th>Court Ref</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayNotes.map((note) => (
                  <tr
                    key={note._id}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/home/notes/details/${note._id}`, {
                        state: {
                          noteData: note,
                          parties:
                            caseData?.[note.courtRef]?.case?.parties || null,
                        },
                      })
                    }
                  >
                    <td>{note.title}</td>
                    <td>{formatDate(note.createdAt)}</td>
                    <td
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/home/cases/casedetails/${note.courtRef}`);
                      }}
                      title={
                        note.courtRef &&
                        caseData?.[note.courtRef]?.case?.parties
                          ? WrittenFormat(caseData[note.courtRef].case.parties)
                          : note.courtRef
                          ? "Parties not available"
                          : ""
                      }
                    >
                      {note.courtRef || "—"}
                    </td>
                    <td>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(note);
                        }}
                      >
                        Update
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(note._id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {(pagination.totalNotes > pagination.limit || pagination.page > 0) && (
        <div className="pagination-controls">
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(0)}
          >
            First
          </button>
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page + 1} of{" "}
            {Math.ceil(pagination.totalNotes / pagination.limit)}
          </span>
          <button
            disabled={
              pagination.page >=
              Math.ceil(pagination.totalNotes / pagination.limit) - 1
            }
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
          <button
            disabled={
              pagination.page >=
              Math.ceil(pagination.totalNotes / pagination.limit) - 1
            }
            onClick={() =>
              handlePageChange(
                Math.ceil(pagination.totalNotes / pagination.limit) - 1
              )
            }
          >
            Last
          </button>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        itemType="this note"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};
